import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import iziToast from 'izitoast';

import env from '@/env';
import { ApiResponse } from '@/types/types';
import { postRefreshTokenEndpoint } from '@/utils/endpoints';

// Set by the store, which imports this file, so this file never imports the store back.
let onRefreshed: () => void = () => {};
let onExpired: () => void = () => {};
export const onAuthChange = (handlers: { refreshed: typeof onRefreshed; expired: typeof onExpired }) => {
  ({ refreshed: onRefreshed, expired: onExpired } = handlers);
};

interface RetriedRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const api = axios.create({
  baseURL: env.VITE_API_HOST,
  headers: { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/json' },
  withCredentials: true,
});

let refreshTokenPromise: Promise<void> | null = null;
let showingExpiredToast = false;

/** One refresh however many requests fail at once. */
async function refreshAuthToken(): Promise<void> {
  if (refreshTokenPromise) return refreshTokenPromise;

  refreshTokenPromise = (async () => {
    try {
      // Marked retried, so a 403 from the refresh itself (both tokens expired) does not refresh again.
      const config = postRefreshTokenEndpoint() as RetriedRequestConfig;
      config._retry = true;
      if ((await api.request(config)).data.status !== 1) throw new Error('Could not refresh the session');

      onRefreshed();
    } finally {
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
}

// A 403 refreshes the token and retries once; a second one means the session is over.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const request = error.config as RetriedRequestConfig | undefined;
    if (!request) return Promise.reject(error);

    const status = error.response?.status;
    if (status === 403) {
      if (request._retry) {
        onExpired();
        return Promise.reject(error);
      }

      request._retry = true;
      try {
        await refreshAuthToken();
        return api.request(request);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        if (!showingExpiredToast) {
          showingExpiredToast = true;
          iziToast.error({ message: 'Your session has expired.', onClosed: () => (showingExpiredToast = false) });
        }

        onExpired();
        return Promise.reject(refreshError);
      }
    }

    if (error.response) {
      console.error('API error:', status, error.response.data);
    } else if (error.request) {
      console.error('Network error:', error.message);
      iziToast.error({ message: 'Network error. Please check your connection.' });
    }

    return Promise.reject(error);
  }
);

export function getDataFromError<T extends ApiResponse>(error: AxiosError): T | null {
  if (!error.response) {
    console.error(error);
    return null;
  }
  return (error.response as AxiosResponse<T>)?.data || null;
}

/** The server's message on a failed request, else the fallback. */
export const errorMessage = (e: unknown, fallback: string): string =>
  (e as AxiosError<ApiResponse> | undefined)?.response?.data?.message || fallback;

export default api;
